import React, { useEffect, useRef, useState } from "react";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import {
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import { Button } from "@mui/material";
import { DateTime } from "luxon";

interface GraphTimePickerProps {
  startDateTime: DateTime;
  endDateTime: DateTime;
  setStartDateTime: (date: DateTime) => void;
  setEndDateTime: (date: DateTime) => void;
  selectedRange: string;
  setSelectedRange: (label: string) => void;
  isLoading?: boolean;
}

const timeRanges = [
  { label: "Last 1 hour", hours: 1 },
  { label: "Last 6 hours", hours: 6 },
  { label: "Last 12 hours", hours: 12 },
  { label: "Last 24 hours", hours: 24 },
  { label: "Yesterday", hours: null },
  { label: "Last 3 days", hours: 72 },
  { label: "Last 7 days", hours: 168 },
  { label: "Last 30 days", hours: 720 },
  { label: "Custom", hours: null },
];

const tz = "America/New_York";

const getRangeDates = (label: string) => {
  const now = DateTime.now().setZone(tz);
  switch (label) {
    case "Last 1 hour":
      return { start: now.minus({ hours: 1 }), end: now };
    case "Last 6 hours":
      return { start: now.minus({ hours: 6 }), end: now };
    case "Last 12 hours":
      return { start: now.minus({ hours: 12 }), end: now };
    case "Last 24 hours":
      return { start: now.minus({ hours: 24 }), end: now };
    case "Yesterday": {
      const todayStart = now.startOf("day");
      return { start: todayStart.minus({ days: 1 }), end: todayStart };
    }
    case "Last 3 days":
      return { start: now.minus({ hours: 72 }), end: now };
    case "Last 7 days":
      return { start: now.minus({ hours: 168 }), end: now };
    case "Last 30 days":
      return { start: now.minus({ hours: 720 }), end: now };
    default:
      return null;
  }
};

const GraphTimePicker: React.FC<GraphTimePickerProps> = ({
  startDateTime,
  endDateTime,
  setStartDateTime,
  setEndDateTime,
  selectedRange,
  setSelectedRange,
  isLoading,
}) => {
  const [showCustomPickers, setShowCustomPickers] = useState<boolean>(false);
  const [localCustomStart, setLocalCustomStart] = useState<DateTime | null>(null);
  const [localCustomEnd, setLocalCustomEnd] = useState<DateTime | null>(null);
  const setStartDateTimeRef = useRef(setStartDateTime);
  const setEndDateTimeRef = useRef(setEndDateTime);

  useEffect(() => {
    setStartDateTimeRef.current = setStartDateTime;
    setEndDateTimeRef.current = setEndDateTime;
  }, [setStartDateTime, setEndDateTime]);

  useEffect(() => {
    if (selectedRange === "Custom") {
      setShowCustomPickers(true);
      setLocalCustomStart(startDateTime);
      setLocalCustomEnd(endDateTime);
    } else {
      setShowCustomPickers(false);
    }
  }, [selectedRange, startDateTime, endDateTime]);

  useEffect(() => {
    if (selectedRange === "Custom") return;

    const applyRollingRange = () => {
      const range = getRangeDates(selectedRange);
      if (!range) return;
      setStartDateTimeRef.current(range.start);
      setEndDateTimeRef.current(range.end);
    };

    applyRollingRange();
    const intervalId = window.setInterval(applyRollingRange, 60_000);
    return () => window.clearInterval(intervalId);
  }, [selectedRange]);

  const nowTz = DateTime.now().setZone(tz);
  const maxSelectableDateTime = nowTz;
  const minSelectableDateTime = nowTz.minus({ months: 1 });

  const handleRangeChange = (event: SelectChangeEvent<string>) => {
    const selected = event.target.value;
    const range = timeRanges.find((r) => r.label === selected);

    if (range) {
      setSelectedRange(selected);
      if (range.label === "Custom") {
        setShowCustomPickers(true);
        setLocalCustomStart(startDateTime);
        setLocalCustomEnd(endDateTime);
      } else if (range.hours !== null || range.label === "Yesterday") {
        setShowCustomPickers(false);
        const nextRange = getRangeDates(range.label);
        if (nextRange) {
          setStartDateTime(nextRange.start);
          setEndDateTime(nextRange.end);
        }
      }
    }
  };

  const handleStartDateTimeChange = (newValue: DateTime | null) => {
    if (newValue) setLocalCustomStart(newValue.setZone(tz));
  };

  const handleEndDateTimeChange = (newValue: DateTime | null) => {
    if (newValue) setLocalCustomEnd(newValue.setZone(tz));
  };

  const applyCustomRange = () => {
    if (localCustomStart && localCustomEnd) {
      const clampedStart = DateTime.max(
        minSelectableDateTime,
        DateTime.min(localCustomStart, maxSelectableDateTime)
      );
      const clampedEnd = DateTime.max(
        minSelectableDateTime,
        DateTime.min(localCustomEnd, maxSelectableDateTime)
      );
      setStartDateTime(clampedStart);
      setEndDateTime(clampedEnd);
    }
  };

  return (
    <div style={{ display: "flex", gap: "16px", alignItems: "center" }}>
      <FormControl size="small">
        <InputLabel id="fpm-time-range-label">Time Range</InputLabel>
        <Select
          labelId="fpm-time-range-label"
          value={selectedRange}
          onChange={handleRangeChange}
          label="Time Range"
        >
          {timeRanges.map((range) => (
            <MenuItem key={range.label} value={range.label}>
              {range.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {isLoading && <CircularProgress size={20} sx={{ color: 'grey.500' }} />}

      {showCustomPickers && (
        <LocalizationProvider dateAdapter={AdapterLuxon}>
          <DateTimePicker
            label="Start DateTime"
            value={localCustomStart ?? startDateTime}
            onChange={handleStartDateTimeChange}
            minDateTime={minSelectableDateTime}
            maxDateTime={maxSelectableDateTime}
          />
          <DateTimePicker
            label="End DateTime"
            value={localCustomEnd ?? endDateTime}
            onChange={handleEndDateTimeChange}
            minDateTime={minSelectableDateTime}
            maxDateTime={maxSelectableDateTime}
          />
          <Button
            variant="contained"
            size="small"
            onClick={applyCustomRange}
            disabled={!localCustomStart || !localCustomEnd}
          >
            Apply
          </Button>
        </LocalizationProvider>
      )}
    </div>
  );
};

export default GraphTimePicker;
